<template>
  <div class="card p-4 shadow-sm">
    <div class="container-fluid my-5">
      <h3 class="text-center mb-4">Gestión de Reservas</h3>

      <!-- 🔍 Filtros -->
      <div class="row mb-4 g-2 justify-content-center">
        <div class="col-md-5">
          <label class="form-label">Buscar por nombre, apellido o código</label>
          <input
            type="text"
            class="form-control"
            placeholder="Buscar..."
            v-model="busqueda"
            @input="filtrarPorTexto"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label">Nº Habitación</label>
          <div class="input-group">
            <input
              type="text"
              class="form-control"
              placeholder="Ej: 101"
              v-model="filtroHabitacion"
              @keyup.enter="filtrarPorHabitacion"
            />
            <button class="btn btn-outline-secondary" @click="filtrarPorHabitacion" title="Buscar">
              <i class="bi bi-search"></i>
            </button>
            <button v-if="filtroHabitacion" class="btn btn-outline-danger" @click="limpiarFiltroHabitacion" title="Limpiar">
              <i class="bi bi-x"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- 📋 Tabla -->
      <div class="table-responsive" v-if="reservas && reservas.length">
        <table class="table table-bordered table-hover align-middle">
          <thead class="bg-light text-center">
            <tr>
              <th>Código</th>
              <th>Habitación(es)</th>
              <th>Fecha Llegada</th>
              <th>Fecha Salida</th>
              <th>Cliente</th>
              <th>Total (Q)</th>
              <th>Saldo Pendiente</th>
              <th>Estado de Pago</th>
              <th>Estado Reserva</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="reserva in reservas" :key="reserva.IdReserva">
              <td>R00{{ reserva.IdReserva }}</td>
              <td>
                <span v-if="reserva.habitaciones_info && reserva.habitaciones_info.length">
                  {{ reserva.habitaciones_info.map(h => h.Numero).join(', ') }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td class="nowrap">{{ formatearFecha(reserva.FechaLlegada) }}</td>
              <td class="nowrap">{{ formatearFecha(reserva.FechaSalida) }}</td>
              <td>{{ reserva.huesped?.Nombre }} {{ reserva.huesped?.Apellido }}</td>
              <td>Q.{{ Number(reserva.TotalReserva || 0).toFixed(2) }}</td>
              <td>Q.{{ Number(reserva.SaldoPendiente || 0).toFixed(2) }}</td>
              <td>
                <span :class="reserva.SaldoPendiente == 0 ? 'badge bg-success' : 'badge bg-danger'">
                  {{ reserva.SaldoPendiente == 0 ? 'Pagado' : 'Pendiente' }}
                </span>
              </td>
              <td>
                <span :class="{
                  'badge bg-warning text-dark': reserva.EstadoReserva === 0,
                  'badge bg-primary': reserva.EstadoReserva === 1,
                  'badge bg-success': reserva.EstadoReserva === 2,
                  'badge bg-danger': reserva.EstadoReserva === 3,
                }">
                  {{ ['Pendiente', 'En curso', 'Finalizada', 'Cancelada'][reserva.EstadoReserva] }}
                </span>
              </td>

              <td>
                <div class="d-flex flex-wrap gap-2 justify-content-center">
                  <button
                    class="btn btn-sm btn-outline-dark"
                    :disabled="reserva.EstadoReserva === 3"
                    @click="reducirReserva(reserva)"
                  >
                    ✂ Reducir
                  </button>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    @click="abrirModalCancelar(reserva)"
                    :disabled="reserva.EstadoReserva !== 0"
                  >
                    ✖ Cancelar
                  </button>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    @click="abrirModalEliminar(reserva)"
                  >
                    🗑️ Eliminar
                  </button>
                  <button
                    class="btn btn-sm btn-outline-primary"
                    :disabled="reserva.EstadoReserva === 3"
                    @click="cambiarHabitacion(reserva)"
                  >
                    🔄 Cambiar Habitación
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="text-center text-muted fs-5">
        No se encontraron reservas.
      </div>

      <!-- 🔽 Paginación -->
      <div v-if="pagination.total > 10" class="mt-4">
        <nav class="d-flex justify-content-center">
          <ul class="pagination shadow-sm">
            <li :class="['page-item', { disabled: !pagination.prev_page_url }]">
              <button class="page-link" @click="cambiarPagina(pagination.current_page - 1)">
                ← Anterior
              </button>
            </li>

            <li
              v-for="page in paginas"
              :key="page"
              :class="['page-item', { active: page === pagination.current_page }]"
            >
              <button class="page-link" @click="cambiarPagina(page)">{{ page }}</button>
            </li>

            <li :class="['page-item', { disabled: !pagination.next_page_url }]">
              <button class="page-link" @click="cambiarPagina(pagination.current_page + 1)">
                Siguiente →
              </button>
            </li>
          </ul>
        </nav>

        <div class="text-center text-muted mt-2">
          Mostrando {{ pagination.from }}–{{ pagination.to }} de {{ pagination.total }} resultados
        </div>
      </div>
    </div>
  </div>

  <!-- ✅ Toast -->
  <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1055;">
    <div v-if="mostrarToast" class="toast show text-white" :class="toastColor">
      <div class="d-flex">
        <div class="toast-body">
          <i :class="iconoToast" class="me-2"></i> {{ alerta.mensaje }}
        </div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" @click="cerrarToast"></button>
      </div>
    </div>
  </div>

  <!-- ✅ Modal Confirmación Cancelar -->
  <div
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    v-if="modalCancelarVisible"
    style="background-color: rgba(0, 0, 0, 0.5);"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content bg-white shadow">
        <div class="modal-header">
          <h5 class="modal-title">Cancelar Reserva</h5>
          <button type="button" class="btn-close" @click="modalCancelarVisible = false"></button>
        </div>
        <div class="modal-body">
          <p>¿Está seguro que desea cancelar esta reserva?</p>
          <p>
            <strong>Cliente:</strong>
            {{ reservaSeleccionada?.huesped?.Nombre }}
            {{ reservaSeleccionada?.huesped?.Apellido }}
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="modalCancelarVisible = false">No</button>
          <button class="btn btn-danger" @click="confirmarCancelacion">Sí, Cancelar</button>
        </div>
      </div>
    </div>
  </div>

  <!-- ✅ Modal Confirmación Eliminar -->
  <div
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    v-if="modalEliminarVisible"
    style="background-color: rgba(0, 0, 0, 0.5);"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content bg-white shadow">
        <div class="modal-header bg-danger text-white">
          <h5 class="modal-title">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>Eliminar Reserva
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="modalEliminarVisible = false"></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-warning">
            <i class="bi bi-info-circle me-2"></i>
            <strong>Advertencia:</strong> Esta acción no se puede deshacer.
          </div>
          <p>¿Está seguro que desea eliminar esta reserva?</p>
          <div class="card">
            <div class="card-body">
              <p class="mb-2">
                <strong>Código:</strong> R00{{ reservaSeleccionada?.IdReserva }}
              </p>
              <p class="mb-0">
                <strong>Cliente:</strong>
                {{ reservaSeleccionada?.huesped?.Nombre }}
                {{ reservaSeleccionada?.huesped?.Apellido }}
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="modalEliminarVisible = false">Cancelar</button>
          <button class="btn btn-danger" @click="confirmarEliminacion">
            <i class="bi bi-trash me-1"></i>Eliminar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { API_URL } from '@/config/config.js'

export default {
  name: 'ReservasReducir',
  data() {
    return {
      reservas: [],
      busqueda: '',
      filtroHabitacion: '',
      debounceTimeout: null,
      modalCancelarVisible: false,
      modalEliminarVisible: false,
      reservaSeleccionada: null,
      alerta: { mensaje: '', tipo: 'success' },
      mostrarToast: false,
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10
      },
      filtrosActivos: {}
    }
  },
  computed: {
    toastColor() {
      return {
        success: 'bg-success',
        danger: 'bg-danger',
        warning: 'bg-warning text-dark',
        info: 'bg-info text-dark'
      }[this.alerta.tipo] || 'bg-primary'
    },
    iconoToast() {
      return {
        success: 'bi bi-check-circle-fill',
        danger: 'bi bi-x-circle-fill',
        warning: 'bi bi-exclamation-triangle-fill',
        info: 'bi bi-info-circle-fill'
      }[this.alerta.tipo] || 'bi bi-info-circle-fill'
    },
    paginas() {
      const total = this.pagination.last_page
      const actual = this.pagination.current_page
      const rango = 2
      const inicio = Math.max(1, actual - rango)
      const fin = Math.min(total, actual + rango)
      const paginas = []
      for (let i = inicio; i <= fin; i++) paginas.push(i)
      return paginas
    }
  },
  mounted() {
    this.buscarReservas({})
  },
  methods: {
    async buscarReservas(filtros = {}, page = 1) {
      try {
        const res = await axios.get(`${API_URL}/reservas/buscar-avanzado`, {
          params: { ...filtros, page }
        })
        this.reservas = res.data.data
        this.pagination = res.data
        this.filtrosActivos = filtros
      } catch (error) {
        console.error('Error al buscar reservas:', error.response?.data || error.message)
        this.reservas = []
      }
    },
    cambiarPagina(page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.buscarReservas(this.filtrosActivos, page)
      }
    },
    filtrarPorTexto() {
      clearTimeout(this.debounceTimeout)
      this.filtroHabitacion = ''
      this.debounceTimeout = setTimeout(() => {
        const query = this.busqueda.trim()
        if (query) {
          this.buscarReservas({ q: query })
        } else {
          this.buscarReservas({})
        }
      }, 500)
    },
    filtrarPorHabitacion() {
      const num = this.filtroHabitacion.trim()
      this.busqueda = ''
      if (num) {
        this.buscarReservas({ habitacion: num })
      } else {
        this.buscarReservas({})
      }
    },
    limpiarFiltroHabitacion() {
      this.filtroHabitacion = ''
      this.buscarReservas({})
    },
    formatearFecha(fecha) {
      return fecha ? fecha.split('T')[0] : ''
    },
    reducirReserva(reserva) {
      this.$router.push({ name: 'ReducirReserva', params: { id: reserva.IdReserva } })
    },
    cambiarHabitacion(reserva) {
      this.$router.push({ name: 'CambiarHabitacion', params: { id: reserva.IdReserva } })
    },
    abrirModalCancelar(reserva) {
      this.reservaSeleccionada = reserva
      this.modalCancelarVisible = true
    },
    abrirModalEliminar(reserva) {
      this.reservaSeleccionada = reserva
      this.modalEliminarVisible = true
    },
    async confirmarCancelacion() {
      try {
        await axios.put(`${API_URL}/reservas/${this.reservaSeleccionada.IdReserva}/cancelar`)
        this.modalCancelarVisible = false
        this.mostrarToastMensaje('Reserva cancelada correctamente', 'success')
        this.buscarReservas({ q: this.busqueda })
      } catch (error) {
        const msg = error.response?.data?.message || 'Error al cancelar la reserva'
        this.mostrarToastMensaje(msg, 'danger')
      }
    },
    async confirmarEliminacion() {
      try {
        await axios.delete(`${API_URL}/reservas/${this.reservaSeleccionada.IdReserva}`)
        this.modalEliminarVisible = false
        this.mostrarToastMensaje('Reserva eliminada correctamente', 'success')
        this.buscarReservas({ q: this.busqueda })
      } catch (error) {
        const msg = error.response?.data?.message || 'Error al eliminar la reserva'
        this.mostrarToastMensaje(msg, 'danger')
      }
    },
    mostrarToastMensaje(mensaje, tipo = 'success') {
      this.alerta.mensaje = mensaje
      this.alerta.tipo = tipo
      this.mostrarToast = true
      setTimeout(() => (this.mostrarToast = false), 3000)
    },
    cerrarToast() {
      this.mostrarToast = false
    }
  }
}
</script>

<style scoped>
.table th,
.table td {
  vertical-align: middle;
  text-align: center;
}
.btn {
  min-width: 130px;
}
.nowrap {
  white-space: nowrap;
}
.pagination .page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
  color: white;
}
.pagination .page-link {
  cursor: pointer;
  border-radius: 6px;
  margin: 0 2px;
}
</style>
